import { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import { ContentProvider, useContent } from "@/lib/content";
import { EnquiryProvider } from "@/components/EnquiryModal";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import DestinationListing from "@/pages/DestinationListing";
import DestinationDetail from "@/pages/DestinationDetail";
import CategoryPackages from "@/pages/CategoryPackages";
import PackageDetail from "@/pages/PackageDetail";
import PackagesBrowse from "@/pages/PackagesBrowse";
import GroupTours from "@/pages/GroupTours";
import VisaListing from "@/pages/VisaListing";
import VisaDetail from "@/pages/VisaDetail";
import FlightsHotels from "@/pages/FlightsHotels";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import SearchResults from "@/pages/SearchResults";
import Legal from "@/pages/Legal";
import { IMG } from "@/lib/site";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let rafId;
    const raf = (time) => { lenis.raf(time); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);
}

function AppRoutes() {
  const { loading, error, reload } = useContent();

  if (error) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-testid="content-error">
        <p className="text-slate-600">{error}</p>
        <button onClick={reload} data-testid="content-retry-button"
                className="mt-4 rounded-full bg-navy px-6 py-3 text-sm font-bold text-white">Retry</button>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/international-holidays" element={
        <DestinationListing region="International" overline="Beyond Borders" title="International Holidays"
          description="From Singapore's skyline to Switzerland's glaciers — handpicked international destinations with visas, flights and stays handled end to end."
          image={IMG.europe} seoTitle="International Holidays" seoDesc="Browse D Travel Link international holiday destinations — Singapore, Dubai, Maldives, Bali, Thailand, Europe and more. Custom quotes on request." />} />
      <Route path="/india-holidays" element={
        <DestinationListing region="India" overline="Incredible India" title="India Holidays"
          description="Kashmir to Kanyakumari, Rajasthan to the Northeast — discover India's finest holiday regions, planned personally."
          image={IMG.taj} seoTitle="India Holidays" seoDesc="Browse D Travel Link India holiday destinations — Kashmir, Kerala, Rajasthan, Goa, Himachal, Andaman and more. Custom quotes on request." />} />
      <Route path="/destinations/:slug" element={<DestinationDetail />} />
      <Route path="/packages" element={<PackagesBrowse />} />
      <Route path="/packages/:slug" element={<PackageDetail />} />
      <Route path="/honeymoon" element={
        <CategoryPackages testid="honeymoon-page"
          match={(p) => (p.holiday_types || []).includes("Honeymoon")}
          overline="Romantic Escapes" title="Honeymoon Specials"
          description="Private pool villas, sunset cruises and slow island mornings — honeymoon packages to Maldives, Bali, Mauritius, Switzerland and beyond."
          image={IMG.maldives} seoTitle="Honeymoon Packages" seoDesc="Romantic honeymoon packages to Maldives, Bali, Mauritius, Thailand, Switzerland and more — customised quotes on request." />} />
      <Route path="/family-holidays" element={
        <CategoryPackages testid="family-page" showDurationFilter
          match={(p) => (p.holiday_types || []).includes("Family")}
          overline="Memorable Moments" title="Family Holidays"
          description="Kid-approved hotels, gentle pacing and experiences every generation enjoys — filter by trip length to find your fit."
          image={IMG.singapore2} seoTitle="Family Holidays" seoDesc="Family holiday packages by duration — 3-5 days, 6-8 days and 9+ days. Customised family quotes on request." />} />
      <Route path="/adventure" element={
        <CategoryPackages testid="adventure-page"
          match={(p) => (p.holiday_types || []).includes("Adventure") || (p.travel_styles || []).includes("Adventure")}
          overline="Thrills & Trails" title="Adventure Tours"
          description="Paragliding over Solang, snorkelling off Havelock, rafting the Ganga — adventure holidays with safety-first planning."
          image={IMG.hiking} seoTitle="Adventure Tours" seoDesc="Adventure holiday packages — paragliding, snorkelling, rafting and Himalayan trails. Custom quotes on request." />} />
      <Route path="/luxury-escapes" element={
        <CategoryPackages testid="luxury-page"
          match={(p) => (p.travel_styles || []).includes("Luxury")}
          overline="5-Star Concierge" title="Luxury Escapes"
          description="Overwater villas, alpine rail journeys and private transfers — when only the finest will do."
          image={IMG.resort} seoTitle="Luxury Escapes" seoDesc="Luxury holiday packages — Maldives villas, Swiss Alps, premium city stays. Custom quotes on request." />} />
      <Route path="/group-tours" element={<GroupTours />} />
      <Route path="/visa" element={<VisaListing />} />
      <Route path="/visa/:slug" element={<VisaDetail />} />
      <Route path="/flights-hotels" element={<FlightsHotels />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/search" element={<SearchResults />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/terms-and-conditions" element={<Legal kind="terms" />} />
      <Route path="/privacy-policy" element={<Legal kind="privacy" />} />
      <Route path="/cancellation-policy" element={<Legal kind="cancellation" />} />
      <Route path="*" element={
        <div className="py-32 text-center" data-testid="not-found-page">
          <h1 className="font-heading text-4xl font-extrabold text-slate-900">404 — Lost your way?</h1>
          <p className="mt-3 text-slate-500">This page doesn't exist, but your dream holiday does.</p>
          <a href="/" className="mt-6 inline-block rounded-full bg-navy px-7 py-3 text-sm font-bold text-white">Back to Home</a>
        </div>} />
    </Routes>
  );
}

function App() {
  useLenis();
  return (
    <div className="App">
      <BrowserRouter>
        <ContentProvider>
          <EnquiryProvider>
            <ScrollToTop />
            <Header />
            <main>
              <AppRoutes />
            </main>
            <Footer />
            <WhatsAppFloat />
            <Toaster position="top-center" richColors />
          </EnquiryProvider>
        </ContentProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
